import { resourceText } from 'src/app/shared/services/localization';
import { AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GoogleMapsService } from './google-maps.service';

// Google supplies suggestions; the person can review and edit every address field.
export function googleAddress(place: any): Record<string,string> | null {
  const parts = place.addressComponents || [];
  const part = (type: string, short = false) => {
    const component = parts.find((item: any) => item.types?.includes(type));
    return (short ? component?.shortText : component?.longText) || '';
  };
  const route = part('route');
  if (!route) return null; // A city or region alone must not replace a street address.
  const country = part('country', true).toUpperCase();
  return {
    street: [part('street_number'), route].filter(Boolean).join(' '),
    city: part('postal_town') || part('locality') || part('sublocality_level_1') || part('administrative_area_level_2'),
    state: part('administrative_area_level_1', true),
    zip: part('postal_code'),
    country
  };
}

@Component({selector:'app-address-autocomplete', standalone:false, template:`
  <section class="address-search" [attr.aria-label]="'FIND_YOUR_ADDRESS' | translate">
    <p class="mb-2 font-weight-bold">{{ 'FIND_YOUR_ADDRESS' | translate }}</p>
    <div #searchHost></div>
    <p class="small text-muted mt-2 mb-0">{{ 'START_TYPING_AND_CHOOSE_A_SUGGESTION_OR_ENTER_YOUR_ADDRESS_IN_THE_FIELDS' | translate }}</p>
    <p *ngIf="message" class="small mt-2 mb-0" role="status">{{message}}</p>
  </section>`, styles:[`
    :host{display:block;margin-bottom:1.25rem;min-width:0;}
    .address-search{padding:1rem;border:1px solid #ddd;border-radius:10px;}
    gmp-place-autocomplete{width:100%;max-width:100%;color-scheme:light;}
  `]})
export class AddressAutocompleteComponent implements AfterViewInit, OnDestroy {
  @Input() form: FormGroup;
  @Input() autofocus = false;
  @ViewChild('searchHost') host: ElementRef<HTMLElement>;
  message = '';
  selecting = false;
  private widget: any;
  private subscription: Subscription;
  private generation = 0;
  private destroyed = false;
  private applying = false;
  constructor(private maps: GoogleMapsService, private zone: NgZone) {}
  async ngAfterViewInit() {
    this.subscription = this.form.valueChanges.subscribe(() => {
      if (this.applying) return;
      this.generation++; this.message = ''; this.selecting = false;
      if (this.widget) this.widget.includedRegionCodes = this.regions();
    });
    try {
      const maps = await this.maps.load();
      const {PlaceAutocompleteElement} = await maps.importLibrary('places');
      if (this.destroyed) return;
      this.widget = new PlaceAutocompleteElement({includedRegionCodes:this.regions(), includedPrimaryTypes:['street_address','premise','subpremise','route']});
      this.widget.placeholder = resourceText('START_TYPING_YOUR_STREET_ADDRESS');
      this.widget.setAttribute('aria-label','Search for your address');
      this.widget.addEventListener('gmp-select', this.select);
      this.widget.addEventListener('gmp-error', this.failed);
      this.host.nativeElement.appendChild(this.widget);
      // Do not steal focus if the person started entering the address while Google loaded.
      if (this.autofocus && !document.activeElement?.matches('input, textarea, select, [contenteditable="true"]')) {
        this.widget.focus({preventScroll:true});
      }
    } catch { if (!this.destroyed) this.zone.run(() => this.message = resourceText('ADDRESS_SEARCH_IS_UNAVAILABLE_RIGHT_NOW_YOU_CAN_ENTER_YOUR_ADDRESS_BELOW')); }
  }
  private regions() { const country = this.form.get('country')?.value; return country ? [String(country).toLowerCase()] : []; }
  private failed = () => this.zone.run(() => this.message = resourceText('ADDRESS_SEARCH_IS_UNAVAILABLE_RIGHT_NOW_YOU_CAN_ENTER_YOUR_ADDRESS_BELOW'));
  private select = async (event: any) => {
    const generation = ++this.generation;
    this.selecting = true;
    let timer: ReturnType<typeof setTimeout>;
    this.zone.run(() => this.message = resourceText('GETTING_YOUR_ADDRESS'));
    try {
      const place = event.placePrediction.toPlace();
      // Only address fields: no map load, contact data or additional coordinate lookup.
      await Promise.race([place.fetchFields({fields:['addressComponents']}), new Promise((_,reject) => { timer = setTimeout(() => reject(new Error('Address lookup timed out')),8000); })]);
      if (this.destroyed || generation !== this.generation) return;
      const address = googleAddress(place);
      if (!address || address.country !== String(this.form.get('country')?.value || '').toUpperCase()) {
        this.zone.run(() => this.message = resourceText('PLEASE_CHOOSE_A_STREET_ADDRESS_IN_YOUR_SELECTED_COUNTRY_OR_ENTER_IT_BELO')); return;
      }
      this.zone.run(() => {
        this.applying = true;
        try { this.form.patchValue(address); this.form.markAsDirty(); }
        finally { this.applying = false; }
        this.message = resourceText('ADDRESS_FILLED_IN_PLEASE_CHECK_THE_DETAILS_BELOW_AND_ADD_AN_APARTMENT_OR');
      });
    } catch { if (!this.destroyed && generation === this.generation) this.failed(); }
    finally { clearTimeout(timer); if (!this.destroyed && generation === this.generation) this.zone.run(() => this.selecting = false); }
  };
  ngOnDestroy() {
    this.destroyed = true; this.generation++; this.subscription?.unsubscribe();
    this.widget?.removeEventListener('gmp-select',this.select);
    this.widget?.removeEventListener('gmp-error',this.failed);
    this.widget?.remove();
  }
}

